<script setup>
	import { computed, ref } from 'vue';
	import { DsText, DsButton, DsButtonPlay, dsUseIsMobile, DsTag, DsBlock } from '@coloplast/design-system';
	import '@coloplast/design-system/dist/components/button/ds-button-play.css';
	import ContentIconText from 'shared/components/contentLibrary/contentIconText.vue';
	import VideoPopup from 'shared/components/VideoElements/videoPopup.vue';
	import ZoomInModalButton from 'shared/components/mediaItems/ZoomInModalButton.vue';
	import { track_content_engagement } from 'shared/services/tracking/contentTrackingService';

	const props = defineProps({
		vm: {
			type: Object,
			default: () => {},
		},
		flag: {
			type: String,
			required: true,
		}
	});

	const { isMobile } = dsUseIsMobile();

	//Video Popup
	const videoUrl = ref(null);
	const videoIsPlaying = ref(false);

	const setVideoUrl = (url) => {
		videoUrl.value = url;
		videoIsPlaying.value = true;
		track_content_engagement('video', 'start', props.vm.CurrentBlock.Headline || 'no_data', videoUrl.value);
	};

	const closePopup = () => {
		videoUrl.value = null;
		videoIsPlaying.value = false;
	};

	const src = computed(() => {
		if (props.flag === "33/66") {
			return `${props.vm.BaseViewModel.ImageUrl}?width=872`;
		}
		return `${props.vm.BaseViewModel.ImageUrl}?width=636`;
	});

	const sizes = computed(() => {
		if (props.flag === "33/66") {
			return "(max-width: 768px) 100vw, (max-width: 991px) 50vw, 872px";
		}
		return "(max-width: 768px) 100vw, (max-width: 991px) 50vw, 636px";
	});
</script>

<template>
	<DsBlock
		:background="vm.BaseViewModel.BackgroundColor"
		:paddingTop="vm.CurrentBlock.PaddingTop"
		:paddingBottom="vm.CurrentBlock.PaddingBottom"
		:divider="vm.CurrentBlock.IsDivider == 'True'"
		class="ds-block--grid c-highlight-multimedia"
		:class="{
			'c-highlight-multimedia--two-thirds': flag === '33/66',
			'c-highlight-multimedia--right-aligned': vm.CurrentBlock.MediaAlignType == 1
		}">
			<div
				v-if="vm.BaseViewModel.ImageUrl || vm.BaseViewModel.VideoUrl || vm.CurrentBlock.VideoContentArea"
				class="c-highlight-multimedia__media"
				:class="{
					'c-highlight-multimedia__media--right-aligned': vm.CurrentBlock.MediaAlignType == 1
				}"
			>
				<picture v-if="(vm.BaseViewModel.ImageUrl && flag === '50/50') || (vm.BaseViewModel.ImageUrl && vm.CurrentBlock.LayoutType == 0 && flag === '33/66')">
					<img
						:src="src"
						:srcset="`${vm.BaseViewModel.ImageUrl}?width=600 600w, ${vm.BaseViewModel.ImageUrl}?width=900 900w, ${vm.BaseViewModel.ImageUrl}?width=1200 1200w, ${vm.BaseViewModel.ImageUrl} 1800w`"
						:sizes="sizes"
						:alt="vm.CurrentBlock.ImageAltText || ''"
						class="c-highlight-multimedia__image"
						:class="{ 'c-highlight-multimedia__image--two-thirds' : flag === '33/66' }"
					/>

				</picture>
				<ZoomInModalButton
					v-if="vm.CurrentBlock.EnableImageZoomIn === 'True' && flag === '50/50' && vm.CurrentBlock.LayoutType == 0"
					:image="vm.BaseViewModel.ImageUrl"
					:headline="vm.CurrentBlock.Headline"
					class="c-highlight-multimedia__image-button"
				/>
				<div
					v-if="vm.BaseViewModel.VideoUrl && vm.CurrentBlock.LayoutType != 0 && flag === '50/50'"
					class="c-highlight-multimedia__video"
				>
					<DsButtonPlay
						aria-label-play="Play video"
						:size="isMobile ? 'md' : 'lg'"
						:play-handler="() => setVideoUrl(vm.BaseViewModel.VideoUrl)"
					/>
				</div>
				<slot v-if="vm.CurrentBlock.LayoutType != 0 && flag === '33/66'"></slot>

				<div class="c-highlight-multimedia__caption" :class="{'c-highlight-multimedia__caption--video' : vm.CurrentBlock.LayoutType == 1}">
					<DsText
						v-if="vm.CurrentBlock.UserName"
						v-text="vm.CurrentBlock.UserName"
						text-type="body-xs"
						text-style="medium"
						html-element="span"
					/>
					<DsText
						v-if="vm.CurrentBlock.UserName"
						text-type="body-xs"
						html-element="span"
						class="c-highlight-multimedia__caption-devider"
					>|</DsText>
					<DsText
						v-if="vm.CurrentBlock.UserDetails"
						v-text="vm.CurrentBlock.UserDetails"
						text-type="body-xs"
						html-element="span"
					/>
					<DsText
						v-if="vm.BaseViewModel.CaptionDescriptionText"
						v-text="vm.BaseViewModel.CaptionDescriptionText"
						text-type="body-xs"
						html-element="span"
						class="c-highlight-multimedia__caption-description"
					/>
				</div>

				<div
					v-if="vm.BaseViewModel.VideoUrl && !vm.BaseViewModel.ImageUrl && vm.CurrentBlock.LayoutType != 0"
					class="c-highlight-multimedia__image c-highlight-multimedia__image--fallback"
				>
				</div>
			</div>
			<div
				class="c-highlight-multimedia__content"
				:class="{
					'c-highlight-multimedia__content--right-aligned': vm.CurrentBlock.MediaAlignType == 1
				}"
			>

				<DsTag v-if="vm.CurrentBlock.Tag" :text="vm.CurrentBlock.Tag" dot class="c-highlight-multimedia__tag" />

				<DsText
					v-if="vm.CurrentBlock.Headline"
					html-element="h3"
					text-type="heading-3xl"
					class="c-highlight-multimedia__headline"
					v-text="vm.CurrentBlock.Headline"
					data-testid="highlight-multimedia__headline"
				/>

				<DsText
					v-if="vm.CurrentBlock.Description"
					class="c-highlight-multimedia__description"
					text-type="body-md"
				>
					<span v-html="vm.CurrentBlock.Description" />
				</DsText>


				<template v-if="flag === '50/50'">
					<div
						v-if="vm.BaseViewModel.AnchorLinkUrl && vm.CurrentBlock.AnchorLinkText"
						class="c-highlight-multimedia__link-wrapper"
					>
						<DsButton
							:href="vm.BaseViewModel.AnchorLinkUrl"
							icon-end="navigate-forward"
							variant="link"
						>
							{{ vm.CurrentBlock.AnchorLinkText }}
						</DsButton>
					</div>
				</template>

				<template v-if="flag === '33/66'">
					<div
						v-if="vm.BaseViewModel.CtaButtonLink && vm.BaseViewModel.CtaButtonText"
						class="c-highlight-multimedia__link"
					>
						<DsButton
							:href="vm.BaseViewModel.CtaButtonLink"
							:variant="vm.BaseViewModel.CtaCheckbox ? 'link' : 'primary'"
							:target="vm.BaseViewModel.CtaButtonTarget"
							:icon-end="vm.BaseViewModel.CtaIcon"
						>
							{{ vm.BaseViewModel.CtaButtonText }}
						</DsButton>
					</div>
					<DsText class="c-highlight-multimedia__disclaimer" v-if="vm.CurrentBlock.Disclaimer" text-type="body-xs"><span v-html="vm.CurrentBlock.Disclaimer"></span></DsText>
				</template>

				<div
					v-if="vm.BaseViewModel.KeyFeatures?.length"
					class="c-highlight-multimedia__feature-wrapper"
				>
					<div
						v-for="(keyFeature, index) in vm.BaseViewModel.KeyFeatures"
						:key="`${keyFeature.Text}-${index}`"
						class="c-highlight-multimedia__feature"
					>
						<ContentIconText
							:icon="keyFeature.Icon"
							:size="isMobile ? 'xs' : 'md'"
						>
							{{ keyFeature.Text }}
						</ContentIconText>
					</div>
				</div>
			</div>


		<VideoPopup
			v-if="videoIsPlaying && videoUrl"
			:video-url="videoUrl"
			@close-popup="closePopup"
		/>
	</DsBlock>
</template>
